/**
 * Idiomas de la interfaz. Hoy solo las pantallas NUEVAS (historial, contador, selector de impresora)
 * usan este catálogo; el resto sigue en español hasta la migración completa. Por eso el idioma NO se
 * detecta solo del navegador: se guarda en localStorage ("zebralab_lang") y por defecto es español.
 * Portugués = pt-BR (mercado de Brasil); una revisión de nativo está pendiente.
 */
import React, { createContext, useContext, useMemo, useState } from "react";

export const LANGS = ["es", "pt"];
const LS_KEY = "zebralab_lang";

const MESSAGES = {
    es: {
        "common.close": "Cerrar",
        "common.untitled": "Diseño sin guardar",
        "printer.label": "Impresora",
        "batch.rows.title": "Datos a imprimir: {count}. Desplázate para revisarlas todas.",
        "batch.rows.count_one": "{n} fila",
        "batch.rows.count_other": "{n} filas",
        "auth.logout": "Salir",
        "auth.logoutTitle": "Cerrar la sesión en este navegador",
        "connect.app.title": "Instalar la app (recomendado)",
        "connect.app.step1": "1. Descarga e instala ZebraLab Agent",
        "connect.app.download": "Descargar para {os} · versión {version} · {size}",
        "connect.app.unsigned.mac": "La primera vez macOS dirá que no puede verificar al desarrollador: haz clic derecho sobre la app, elige «Abrir» y confirma «Abrir».",
        "connect.app.unsigned.windows": "La primera vez Windows mostrará «Windows protegió su PC»: pulsa «Más información» y luego «Ejecutar de todas formas».",
        "connect.app.step2": "2. Conecta este equipo",
        "connect.app.connect": "Conectar con la app",
        "connect.app.hint": "Tu navegador preguntará si abre «ZebraLab Agent»: acepta y confirma en la ventana de la app.",
        "connect.app.nothing": "¿No pasó nada? Comprueba que la app esté instalada (paso 1) y vuelve a pulsar el botón.",
        "connect.manual": "Prefiero usar comandos ({shell})",
        "printer.choose": "Elegir impresora",
        "printer.willPrint": "Se imprimirá en: {printer}",
        "printer.notZebra": "«{printer}» no parece una Zebra: las etiquetas ZPL podrían salir como texto o no salir.",
        "printer.auto": "Automática",
        "printer.autoWith": "Automática ({printer})",
        "printer.none": "Sin agente conectado",
        "printer.hint": "«Automática» usa la única impresora Zebra del equipo. Nunca usa la predeterminada.",
        "counter.today": "Hoy",
        "counter.week": "Semana",
        "counter.title": "Etiquetas impresas. Hoy: {today} · semana: {week} · mes: {month}. Clic para ver el historial.",
        "history.open": "Historial",
        "history.title": "Historial de impresión",
        "history.from": "Desde",
        "history.to": "Hasta",
        "history.range.today": "Hoy",
        "history.range.7d": "Últimos 7 días",
        "history.range.month": "Este mes",
        "history.range.all": "Todo",
        "history.printer": "Impresora",
        "history.allPrinters": "Todas",
        "history.status": "Estado",
        "history.allStatus": "Todos",
        "history.search": "Buscar por plantilla…",
        "history.col.date": "Fecha",
        "history.col.template": "Plantilla",
        "history.col.labels": "Etiquetas",
        "history.col.pages": "Hojas",
        "history.col.printer": "Impresora",
        "history.col.computer": "Equipo",
        "history.col.status": "Estado",
        "history.status.done": "Impreso",
        "history.status.error": "Con error",
        "history.status.expired": "Caducado",
        "history.status.cancelled": "Cancelado",
        "history.status.queued": "En cola",
        "history.status.sent": "Enviado",
        "history.empty": "No hay impresiones en este período.",
        "history.loading": "Cargando…",
        "history.loadError": "No se pudo cargar el historial.",
        "history.more": "Cargar más",
        "history.export": "Exportar CSV",
        "history.jobs_one": "{n} impresión",
        "history.jobs_other": "{n} impresiones",
        "history.labels_one": "{n} etiqueta",
        "history.labels_other": "{n} etiquetas",
        "history.pages_one": "{n} hoja",
        "history.pages_other": "{n} hojas",
        "history.summary": "{jobs} · {labels} · {pages} (solo las impresas)",
    },
    pt: {
        "common.close": "Fechar",
        "common.untitled": "Design não salvo",
        "printer.label": "Impressora",
        "batch.rows.title": "Dados a imprimir: {count}. Role para revisar todas.",
        "batch.rows.count_one": "{n} linha",
        "batch.rows.count_other": "{n} linhas",
        "auth.logout": "Sair",
        "auth.logoutTitle": "Encerrar a sessão neste navegador",
        "connect.app.title": "Instalar o app (recomendado)",
        "connect.app.step1": "1. Baixe e instale o ZebraLab Agent",
        "connect.app.download": "Baixar para {os} · versão {version} · {size}",
        "connect.app.unsigned.mac": "Na primeira vez o macOS dirá que não pode verificar o desenvolvedor: clique com o botão direito no app, escolha «Abrir» e confirme «Abrir».",
        "connect.app.unsigned.windows": "Na primeira vez o Windows mostrará «O Windows protegeu seu PC»: clique em «Mais informações» e depois em «Executar assim mesmo».",
        "connect.app.step2": "2. Conecte este computador",
        "connect.app.connect": "Conectar com o app",
        "connect.app.hint": "Seu navegador perguntará se abre o «ZebraLab Agent»: aceite e confirme na janela do app.",
        "connect.app.nothing": "Nada aconteceu? Verifique se o app está instalado (passo 1) e clique no botão de novo.",
        "connect.manual": "Prefiro usar comandos ({shell})",
        "printer.choose": "Escolher impressora",
        "printer.willPrint": "Vai imprimir em: {printer}",
        "printer.notZebra": "«{printer}» não parece uma Zebra: as etiquetas ZPL podem sair como texto ou não sair.",
        "printer.auto": "Automática",
        "printer.autoWith": "Automática ({printer})",
        "printer.none": "Nenhum agente conectado",
        "printer.hint": "«Automática» usa a única impressora Zebra do computador. Nunca usa a padrão do sistema.",
        "counter.today": "Hoje",
        "counter.week": "Semana",
        "counter.title": "Etiquetas impressas. Hoje: {today} · semana: {week} · mês: {month}. Clique para ver o histórico.",
        "history.open": "Histórico",
        "history.title": "Histórico de impressão",
        "history.from": "De",
        "history.to": "Até",
        "history.range.today": "Hoje",
        "history.range.7d": "Últimos 7 dias",
        "history.range.month": "Este mês",
        "history.range.all": "Tudo",
        "history.printer": "Impressora",
        "history.allPrinters": "Todas",
        "history.status": "Status",
        "history.allStatus": "Todos",
        "history.search": "Buscar por modelo…",
        "history.col.date": "Data",
        "history.col.template": "Modelo",
        "history.col.labels": "Etiquetas",
        "history.col.pages": "Folhas",
        "history.col.printer": "Impressora",
        "history.col.computer": "Computador",
        "history.col.status": "Status",
        "history.status.done": "Impresso",
        "history.status.error": "Com erro",
        "history.status.expired": "Expirado",
        "history.status.cancelled": "Cancelado",
        "history.status.queued": "Na fila",
        "history.status.sent": "Enviado",
        "history.empty": "Não há impressões neste período.",
        "history.loading": "Carregando…",
        "history.loadError": "Não foi possível carregar o histórico.",
        "history.more": "Carregar mais",
        "history.export": "Exportar CSV",
        "history.jobs_one": "{n} impressão",
        "history.jobs_other": "{n} impressões",
        "history.labels_one": "{n} etiqueta",
        "history.labels_other": "{n} etiquetas",
        "history.pages_one": "{n} folha",
        "history.pages_other": "{n} folhas",
        "history.summary": "{jobs} · {labels} · {pages} (somente as impressas)",
    },
};

export const LOCALES = { es: "es-CL", pt: "pt-BR" };

export function getLang() {
    try {
        const stored = localStorage.getItem(LS_KEY);
        if (LANGS.includes(stored)) return stored;
    } catch {
        /* sin almacenamiento: español */
    }
    return "es";
}

/** Traduce `key`. Con `params.n` numérico usa `${key}_one` / `${key}_other` según corresponda. */
export function translate(lang, key, params) {
    const dict = MESSAGES[lang] || MESSAGES.es;
    let template;
    if (params && typeof params.n === "number") {
        template = dict[`${key}_${params.n === 1 ? "one" : "other"}`] ?? MESSAGES.es[`${key}_${params.n === 1 ? "one" : "other"}`];
    }
    template = template ?? dict[key] ?? MESSAGES.es[key] ?? key;
    return template.replace(/\{(\w+)\}/g, (_, name) => (params && params[name] !== undefined ? String(params[name]) : ""));
}

const I18nContext = createContext({ lang: "es", t: (key, params) => translate("es", key, params), setLang: () => {} });

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(getLang);
    const value = useMemo(
        () => ({
            lang,
            t: (key, params) => translate(lang, key, params),
            setLang: (next) => {
                if (!LANGS.includes(next)) return;
                try {
                    localStorage.setItem(LS_KEY, next);
                } catch {
                    /* ignore */
                }
                setLangState(next);
            },
        }),
        [lang]
    );
    return React.createElement(I18nContext.Provider, { value }, children);
}

export function useI18n() {
    return useContext(I18nContext);
}

export function useT() {
    return useContext(I18nContext).t;
}
